import { useEffect } from 'react';

/**
 * Page metadata, set from the rendered page.
 *
 * ## Deliberately small
 *
 * A title, a description, a canonical link and a robots directive. No
 * structured-data subsystem, no Open Graph matrix, no sitemap generator —
 * those are a project of their own, and half of one produces markup that makes
 * claims the platform cannot back.
 *
 * ## `noindex` is the part that matters
 *
 * A single-page app serves 200 for every path. Without an explicit directive a
 * crawler indexes the not-found page, the error page and every mistyped URL as
 * though each were a real product page — and then serves them to people. So
 * anything that is not a genuine, resolved, public page says `noindex`
 * explicitly rather than relying on a status code the app never sends.
 *
 * There is no schema.org markup here at all. A `Product` with an `offers` block
 * needs a price, and an unpriced offer would either be omitted — making the
 * markup disagree with the page — or published with a zero, which is a false
 * claim to a search engine as well as to a customer.
 */

export interface PageMeta {
  readonly title: string;
  readonly description?: string;
  /** The path this page should be indexed under, without the query string. */
  readonly canonicalPath?: string;
  /** True for anything that is not a real, public, resolved page. */
  readonly noindex?: boolean;
}

const SITE_NAME = 'Just Treated';

export function usePageMeta(meta: PageMeta): void {
  useEffect(() => {
    document.title = `${meta.title} | ${SITE_NAME}`;

    setMetaTag('description', meta.description);
    setMetaTag('robots', meta.noindex === true ? 'noindex, follow' : 'index, follow');
    setCanonical(meta.canonicalPath);
  }, [meta.title, meta.description, meta.canonicalPath, meta.noindex]);
}

function setMetaTag(name: string, content: string | undefined): void {
  const existing = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);

  if (content === undefined) {
    existing?.remove();
    return;
  }

  const tag = existing ?? document.createElement('meta');
  tag.setAttribute('name', name);
  tag.setAttribute('content', content);
  if (existing === null) document.head.append(tag);
}

/**
 * The canonical URL, without the query string.
 *
 * A search for "ibuprofen" sorted by price is the same page as the same search
 * sorted by relevance; without this, a crawler treats every filter combination
 * as its own page and splits whatever authority the real page has.
 */
function setCanonical(path: string | undefined): void {
  const existing = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');

  if (path === undefined) {
    existing?.remove();
    return;
  }

  const link = existing ?? document.createElement('link');
  link.setAttribute('rel', 'canonical');
  link.setAttribute('href', `${window.location.origin}${path}`);
  if (existing === null) document.head.append(link);
}
